
import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const ScrollReveal = ({ children }) => {
    const containerRef = useRef(null);

    useEffect(() => {
        if (!containerRef.current) return;

        const elements = containerRef.current.querySelectorAll('.reveal-on-scroll');
        const scrollerSet = new Set();
        const listenerMap = new Map();

        elements.forEach((el) => {
            const anim = gsap.fromTo(el, { autoAlpha: 0, y: 50 }, {
                autoAlpha: 1,
                y: 0,
                duration: 1,
                ease: 'power2.out'
            });

            // determine the scrollable parent for this element (falls back to window)
            const getScrollParent = (node) => {
                if (!node) return window;
                let parent = node.parentElement;
                while (parent) {
                    const style = getComputedStyle(parent);
                    const overflow = `${style.overflow}${style.overflowY}${style.overflowX}`;
                    if (/auto|scroll|overlay/.test(overflow)) return parent;
                    parent = parent.parentElement;
                }
                return window;
            };

            const scroller = getScrollParent(el);

            // remember scroller for adding touch/pointer listeners later
            scrollerSet.add(scroller);

            ScrollTrigger.create({
                trigger: el,
                animation: anim,
                scroller: scroller === window ? undefined : scroller,
                start: 'top 100%',
                toggleActions: 'play none none none',
                once: true,
            });
        });

        // ensure ScrollTrigger calculates sizes/positions after layout
        ScrollTrigger.refresh();

        // add lightweight handlers so touch/pointer interactions update/refresh ScrollTrigger
        scrollerSet.forEach((scroller) => {
            const target = scroller === window ? window : scroller;
            const onMove = () => ScrollTrigger.update();
            const onEnd = () => ScrollTrigger.refresh();

            // passive touchmove for performance
            try {
                target.addEventListener('touchmove', onMove, { passive: true });
                target.addEventListener('touchend', onEnd, { passive: true });
            } catch (e) {
                // older browsers
                target.addEventListener('touchmove', onMove);
                target.addEventListener('touchend', onEnd);
            }

            target.addEventListener('pointerup', onEnd);

            listenerMap.set(target, { onMove, onEnd });
        });

        return () => {
            // remove ScrollTrigger instances
            ScrollTrigger.getAll().forEach(trigger => trigger.kill());
            // remove added listeners
            listenerMap.forEach((handlers, target) => {
                try {
                    target.removeEventListener('touchmove', handlers.onMove, { passive: true });
                    target.removeEventListener('touchend', handlers.onEnd, { passive: true });
                } catch (e) {
                    target.removeEventListener('touchmove', handlers.onMove);
                    target.removeEventListener('touchend', handlers.onEnd);
                }
                target.removeEventListener('pointerup', handlers.onEnd);
            });
        };
    }, []);


    return <div ref={containerRef}>{children}</div>;
};

export default ScrollReveal;
